/**
 * NOS-95: Nav context panel - matches BUILDER_DESIGN_SPEC Section 13.
 * Editable links (add, remove, rename), CTA button text. Uses org_navigation for links.
 * NOS-102: Open-in-new-tab toggle with optimistic UI updates.
 * NOS-233: nested-menu editing lives HERE now — nest/un-nest, add dropdown
 * items, parent-delete-promotes-children (ported from the NOS-39/NOS-200
 * NavigationEditor, which was orphaned when NOS-97 removed its mount) — plus
 * the NOS-215 secondary CTA (self-parented row) gets its first edit UI.
 */
import { useEffect, useMemo, useRef, useState } from "react"
import { toast } from "sonner"
import { useNavigationMutations } from "@/hooks/useNavigation"
import { buildNavTree, findSecondaryNavCta, type NavItem } from "@/types/navigation"
import type { PageSummary } from "@/stores/customizationStore"
import { IconBtn } from "./IconBtn"
import { TinyInput } from "./TinyInput"
import { BuilderTooltip } from "./BuilderTooltip"
import { FloatingPanel } from "./ui/FloatingPanel"
import { GROUP_LABEL } from "./ui/GroupLabel"

export interface NavContextPanelProps {
  orgId: string | null
  pages?: PageSummary[]
  navItems: NavItem[]
  navCtaText?: string
  onUpdateNavCtaText?: (text: string) => void
  primaryColor?: string
  onRefresh: () => Promise<void>
  onClose: () => void
  onOpenPageSettings?: () => void
  /** NOS-239: switch to the announcement-bar panel (chrome above the nav). */
  onOpenAnnouncement?: () => void
}

const LABEL_STYLE = GROUP_LABEL

/** Spec Section 13: Nav context panel - width: 300px */
const PANEL_POSITION: React.CSSProperties = {
  top: 100,
  right: 16,
  bottom: 16,
  width: 300,
  zIndex: 1000,
}

export function NavContextPanel({
  orgId,
  pages,
  navItems,
  navCtaText = "Donate",
  onUpdateNavCtaText,
  primaryColor = "#2563eb",
  onRefresh,
  onClose,
  onOpenPageSettings,
  onOpenAnnouncement,
}: NavContextPanelProps) {
  const {
    addNavItem,
    updateNavItem,
    deleteNavItem,
    reorderNavItems,
    loading: mutLoading,
  } = useNavigationMutations(orgId)
  // NOS-102: Optimistic overrides so toggle reflects immediately before refresh
  const [openInNewTabOverrides, setOpenInNewTabOverrides] = useState<Record<string, boolean>>({})
  // Local editing state — prevents DB call + re-render resetting inputs on every keystroke
  const [labelDrafts, setLabelDrafts] = useState<Record<string, string>>({})
  const [hrefDrafts, setHrefDrafts] = useState<Record<string, string>>({})
  useEffect(() => {
    setOpenInNewTabOverrides({})
    // Sync drafts when server data changes (e.g. after save or add)
    setLabelDrafts(Object.fromEntries(navItems.map((i) => [i.id, i.label])))
    setHrefDrafts(Object.fromEntries(navItems.map((i) => [i.id, i.href])))
  }, [navItems])
  const items = useMemo(() => {
    const withOverrides = navItems.map((item) => ({
      ...item,
      open_in_new_tab: item.id in openInNewTabOverrides
        ? openInNewTabOverrides[item.id]
        : Boolean(item.open_in_new_tab),
    }))
    // NOS-188: tree order — each parent followed by its dropdown children
    return buildNavTree(withOverrides).flatMap((n) => {
      const { children, ...parent } = n
      return [parent as NavItem, ...children]
    })
  }, [navItems, openInNewTabOverrides])
  const [addingLink, setAddingLink] = useState(false)
  const [newLabel, setNewLabel] = useState("")
  const [newHref, setNewHref] = useState("")
  const [newOpenInNewTab, setNewOpenInNewTab] = useState(false)
  // NOS-233: when set, the add-link form creates a dropdown item under this parent
  const [addParentId, setAddParentId] = useState<string | null>(null)
  // NOS-233: secondary CTA (NOS-215 self-parented row) editing state
  const secondaryCta = useMemo(() => findSecondaryNavCta(navItems), [navItems])
  const [ctaLabelDraft, setCtaLabelDraft] = useState("")
  const [ctaHrefDraft, setCtaHrefDraft] = useState("")
  const [addingSecondaryCta, setAddingSecondaryCta] = useState(false)
  useEffect(() => {
    setCtaLabelDraft(secondaryCta?.label ?? "")
    setCtaHrefDraft(secondaryCta?.href ?? "")
  }, [secondaryCta])
  // NOS-233: ids of items that have dropdown children (nesting is one level)
  const parentIds = useMemo(
    () => new Set(navItems.filter((i) => i.parent_id && i.parent_id !== i.id).map((i) => i.parent_id as string)),
    [navItems]
  )

  // Drag-to-reorder state
  const [localItems, setLocalItems] = useState<typeof items>(items)
  useEffect(() => { setLocalItems(items) }, [items])
  const dragIndexRef = useRef<number | null>(null)
  const [draggingIndex, setDraggingIndex] = useState<number | null>(null)
  const [dropTargetIndex, setDropTargetIndex] = useState<number | null>(null)

  const handleDragStart = (e: React.DragEvent, index: number) => {
    dragIndexRef.current = index
    setDraggingIndex(index)
    // Required for Firefox
    e.dataTransfer.effectAllowed = "move"
    e.dataTransfer.setData("text/plain", String(index))
  }
  const handleDragEnter = (e: React.DragEvent, index: number) => {
    e.preventDefault()
    setDropTargetIndex(index)
  }
  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault()
    e.dataTransfer.dropEffect = "move"
  }
  const handleDrop = async (e: React.DragEvent, dropIndex: number) => {
    e.preventDefault()
    const fromIndex = dragIndexRef.current
    setDraggingIndex(null)
    setDropTargetIndex(null)
    dragIndexRef.current = null
    if (fromIndex === null || fromIndex === dropIndex) return
    const reordered = [...localItems]
    const [moved] = reordered.splice(fromIndex, 1)
    reordered.splice(dropIndex, 0, moved)
    setLocalItems(reordered)
    const ok = await reorderNavItems(reordered.map((i) => i.id))
    if (ok) await onRefresh()
    else {
      setLocalItems(localItems)
      toast.error("Failed to reorder. Please try again.")
    }
  }
  const handleDragEnd = () => {
    setDraggingIndex(null)
    setDropTargetIndex(null)
    dragIndexRef.current = null
  }

  const handleAddLink = async () => {
    if (!orgId || !newLabel.trim() || !newHref.trim()) return
    setAddingLink(true)
    const href = newHref.startsWith("/") || newHref.startsWith("http") ? newHref : `/${newHref}`
    const added = await addNavItem({
      org_id: orgId,
      label: newLabel.trim(),
      href,
      order_index: items.length,
      open_in_new_tab: newOpenInNewTab,
      // NOS-233: creating a dropdown item nests it under the chosen parent
      ...(addParentId ? { parent_id: addParentId } : {}),
    })
    setAddingLink(false)
    setAddParentId(null)
    if (added) {
      setNewLabel("")
      setNewHref("")
      setNewOpenInNewTab(false)
      await onRefresh()
    }
  }

  const handleToggleOpenInNewTab = async (item: NavItem) => {
    const nextValue = !Boolean(item.open_in_new_tab)
    setOpenInNewTabOverrides((prev) => ({ ...prev, [item.id]: nextValue }))
    const ok = await updateNavItem(item.id, { open_in_new_tab: nextValue })
    await onRefresh()
    if (!ok) {
      setOpenInNewTabOverrides((prev) => {
        const next = { ...prev }
        delete next[item.id]
        return next
      })
      toast.error("Failed to update link. Please try again.")
    }
  }

  const handleLabelBlur = async (item: NavItem) => {
    const label = labelDrafts[item.id] ?? item.label
    if (label === item.label) return
    await updateNavItem(item.id, { label, href: item.href })
    await onRefresh()
  }

  const handleHrefBlur = async (item: NavItem) => {
    const raw = hrefDrafts[item.id] ?? item.href
    const href = raw.startsWith("/") || raw.startsWith("http") ? raw : `/${raw}`
    if (href === item.href) return
    await updateNavItem(item.id, { label: item.label, href })
    await onRefresh()
  }

  const handleDelete = async (id: string) => {
    // NOS-233: parent_id is ON DELETE CASCADE — promote children to top level
    // first so deleting a dropdown parent never silently deletes its items
    // (NavigationEditor's original behavior).
    const children = navItems.filter((i) => i.parent_id === id && i.id !== id)
    for (const child of children) {
      await updateNavItem(child.id, { parent_id: null })
    }
    await deleteNavItem(id)
    await onRefresh()
  }

  /** NOS-233: nest a top-level item under the nearest preceding top-level item. */
  const handleNest = async (item: NavItem, index: number) => {
    for (let i = index - 1; i >= 0; i--) {
      const candidate = localItems[i]
      if (!candidate.parent_id) {
        const ok = await updateNavItem(item.id, { parent_id: candidate.id })
        if (!ok) toast.error("Failed to nest item. Please try again.")
        await onRefresh()
        return
      }
    }
  }

  /** NOS-233: move a dropdown item back to the top level. */
  const handleUnNest = async (item: NavItem) => {
    const ok = await updateNavItem(item.id, { parent_id: null })
    if (!ok) toast.error("Failed to move item. Please try again.")
    await onRefresh()
  }

  /** NOS-233: create / update / remove the secondary CTA (self-parented row). */
  const handleSaveSecondaryCta = async () => {
    if (!orgId || !ctaLabelDraft.trim() || !ctaHrefDraft.trim()) return
    const href =
      ctaHrefDraft.startsWith("/") || ctaHrefDraft.startsWith("http") || ctaHrefDraft.startsWith("#")
        ? ctaHrefDraft
        : `/${ctaHrefDraft}`
    if (secondaryCta) {
      await updateNavItem(secondaryCta.id, { label: ctaLabelDraft.trim(), href })
    } else {
      // NOS-215 convention: self-parented row. Insert, then point parent_id at
      // its own id (insert can't reference the id it is about to create).
      const created = await addNavItem({
        org_id: orgId,
        label: ctaLabelDraft.trim(),
        href,
        order_index: 999,
      })
      if (created) await updateNavItem(created.id, { parent_id: created.id })
      else toast.error("Failed to add the button. Please try again.")
    }
    setAddingSecondaryCta(false)
    await onRefresh()
  }

  const handleRemoveSecondaryCta = async () => {
    if (!secondaryCta) return
    await deleteNavItem(secondaryCta.id)
    setAddingSecondaryCta(false)
    await onRefresh()
  }

  return (
    <FloatingPanel
      className="animate-slideIn"
      position={PANEL_POSITION}
      title="Navigation"
      dotColor={primaryColor}
      onClose={onClose}
    >
      <div
        style={{
          flex: 1,
          overflowY: "auto",
          padding: 16,
          display: "flex",
          flexDirection: "column",
          gap: 16,
        }}
      >
        {/* LINKS */}
        <div>
          <label style={LABEL_STYLE}>Links</label>
          {mutLoading && items.length === 0 ? (
            <div style={{ fontSize: 11, color: "rgba(255,255,255,0.5)" }}>
              Loading…
            </div>
          ) : items.length === 0 && pages && pages.length > 0 ? (
            // No custom links: show auto-page entries read-only
            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              {pages.map((page) => (
                <div
                  key={page.id}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    padding: "8px 10px",
                    background: "rgba(255,255,255,0.03)",
                    border: "1px solid rgba(255,255,255,0.05)",
                    borderRadius: 8,
                    opacity: 0.7,
                  }}
                >
                  <span style={{ flex: 1, fontSize: 12, color: "#fff" }}>
                    {page.title ?? page.slug ?? "Untitled"}
                  </span>
                  <span style={{ fontSize: 10, color: "rgba(255,255,255,0.3)" }}>
                    /{page.slug.replace(/^\//, "")}
                  </span>
                  <BuilderTooltip
                    text="Auto-generated from your site pages."
                    actionLabel={onOpenPageSettings ? "Open Page Settings →" : undefined}
                    onAction={onOpenPageSettings}
                  >
                    <span
                      style={{
                        fontSize: 9,
                        fontWeight: 700,
                        textTransform: "uppercase" as const,
                        letterSpacing: "0.05em",
                        color: "rgba(255,255,255,0.25)",
                        flexShrink: 0,
                        cursor: "help",
                      }}
                    >
                      Auto
                    </span>
                  </BuilderTooltip>
                </div>
              ))}
            </div>
          ) : localItems.length > 0 ? (
            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              {localItems.map((item, index) => (
                <div
                  key={item.id}
                  draggable={!item.parent_id}
                  onDragStart={(e) => handleDragStart(e, index)}
                  onDragEnter={(e) => handleDragEnter(e, index)}
                  onDragOver={handleDragOver}
                  onDrop={(e) => handleDrop(e, index)}
                  onDragEnd={handleDragEnd}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    padding: "8px 10px",
                    // NOS-188: dropdown children indent under their parent
                    marginLeft: item.parent_id ? 18 : 0,
                    background: dropTargetIndex === index && draggingIndex !== index ? "rgba(255,255,255,0.08)" : "rgba(255,255,255,0.04)",
                    border: `1px solid ${dropTargetIndex === index && draggingIndex !== index ? "rgba(255,255,255,0.2)" : "rgba(255,255,255,0.06)"}`,
                    borderRadius: 8,
                    opacity: draggingIndex === index ? 0.4 : 1,
                    transition: "background 0.1s, border-color 0.1s, opacity 0.1s",
                  }}
                >
                  {/* Drag handle (top-level only); children show a nesting marker */}
                  {item.parent_id ? (
                    <span
                      style={{
                        color: "rgba(255,255,255,0.25)",
                        fontSize: 12,
                        lineHeight: 1,
                        flexShrink: 0,
                        userSelect: "none",
                        paddingRight: 2,
                      }}
                      title="Dropdown item"
                    >
                      ↳
                    </span>
                  ) : (
                    <span
                      style={{
                        cursor: "grab",
                        color: "rgba(255,255,255,0.25)",
                        fontSize: 12,
                        lineHeight: 1,
                        flexShrink: 0,
                        userSelect: "none",
                        paddingRight: 2,
                      }}
                      title="Drag to reorder"
                    >
                      ⠿
                    </span>
                  )}
                  <TinyInput
                    value={labelDrafts[item.id] ?? item.label}
                    onChange={(e) => setLabelDrafts((p) => ({ ...p, [item.id]: e.target.value }))}
                    onBlur={() => void handleLabelBlur(item)}
                    placeholder="Label"
                    style={{ flex: 1 }}
                  />
                  <TinyInput
                    value={hrefDrafts[item.id] ?? item.href}
                    onChange={(e) => setHrefDrafts((p) => ({ ...p, [item.id]: e.target.value }))}
                    onBlur={() => void handleHrefBlur(item)}
                    placeholder="/about"
                    style={{ flex: 1, fontSize: 11 }}
                  />
                  <button
                    type="button"
                    title={
                      item.open_in_new_tab
                        ? "Opens in new tab • Click to open in same tab"
                        : "Opens in same tab • Click to open in new tab"
                    }
                    onClick={() => void handleToggleOpenInNewTab(item)}
                    style={{
                      width: 22,
                      height: 22,
                      minWidth: 22,
                      minHeight: 22,
                      padding: 0,
                      cursor: "pointer",
                      border: `1px solid ${item.open_in_new_tab ? primaryColor : "rgba(255,255,255,0.4)"}`,
                      background: item.open_in_new_tab ? primaryColor : "transparent",
                      borderRadius: "50%",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexShrink: 0,
                      color: item.open_in_new_tab ? "#fff" : "rgba(255,255,255,0.5)",
                      fontSize: 12,
                      lineHeight: 1,
                      transition: "all 0.12s",
                    }}
                  >
                    ↗
                  </button>
                  {/* NOS-233: nesting controls — one level, matching NOS-200 */}
                  {item.parent_id ? (
                    <IconBtn
                      size={22}
                      onClick={() => void handleUnNest(item)}
                      title="Move to top level"
                    >
                      ⤴
                    </IconBtn>
                  ) : (
                    <>
                      {index > 0 && !parentIds.has(item.id) && (
                        <IconBtn
                          size={22}
                          onClick={() => void handleNest(item, index)}
                          title="Nest under the item above (creates a dropdown)"
                        >
                          ⤵
                        </IconBtn>
                      )}
                      <IconBtn
                        size={22}
                        onClick={() => {
                          setAddParentId(item.id)
                          setAddingLink(true)
                        }}
                        title="Add a dropdown item under this link"
                      >
                        ＋
                      </IconBtn>
                    </>
                  )}
                  <IconBtn
                    size={22}
                    danger
                    onClick={() => void handleDelete(item.id)}
                  >
                    ✕
                  </IconBtn>
                </div>
              ))}
            </div>
          ) : null}

          {/* + Add link */}
          <button
            type="button"
            onClick={() => {
              setAddParentId(null)
              setAddingLink(!addingLink)
            }}
            style={{
              marginTop: 8,
              width: "100%",
              padding: 7,
              background: "rgba(255,255,255,0.02)",
              border: "1px dashed rgba(255,255,255,0.08)",
              borderRadius: 7,
              color: "rgba(255,255,255,0.3)",
              fontSize: 10,
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 6,
            }}
          >
            <span>＋</span>
            <span>Add link</span>
          </button>
          {addingLink && (
            <div
              style={{
                marginTop: 8,
                padding: 10,
                background: "rgba(255,255,255,0.04)",
                border: "1px solid rgba(255,255,255,0.06)",
                borderRadius: 8,
                display: "flex",
                flexDirection: "column",
                gap: 8,
              }}
            >
              {addParentId && (
                <div style={{ fontSize: 10, color: "rgba(255,255,255,0.45)" }}>
                  Adding a dropdown item under{" "}
                  <strong>
                    {navItems.find((i) => i.id === addParentId)?.label ?? "this link"}
                  </strong>
                </div>
              )}
              <TinyInput
                placeholder="Label (e.g. About)"
                value={newLabel}
                onChange={(e) => setNewLabel(e.target.value)}
              />
              <TinyInput
                placeholder="Href (e.g. /about or https://…)"
                value={newHref}
                onChange={(e) => setNewHref(e.target.value)}
              />
              <label
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  fontSize: 11,
                  color: "rgba(255,255,255,0.5)",
                  cursor: "pointer",
                }}
              >
                <span
                  style={{
                    position: "relative",
                    flexShrink: 0,
                    width: 16,
                    height: 16,
                    boxSizing: "border-box",
                  }}
                >
                  <input
                    type="checkbox"
                    checked={newOpenInNewTab}
                    onChange={(e) => setNewOpenInNewTab(e.target.checked)}
                    style={{
                      position: "absolute",
                      opacity: 0,
                      width: 16,
                      height: 16,
                      margin: 0,
                      cursor: "pointer",
                      left: 0,
                      top: 0,
                    }}
                  />
                  <span
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      justifyContent: "center",
                      width: 16,
                      height: 16,
                      minWidth: 16,
                      minHeight: 16,
                      boxSizing: "border-box",
                      padding: 0,
                      background: newOpenInNewTab
                        ? primaryColor
                        : "rgba(255,255,255,0.04)",
                      border: `1px solid ${newOpenInNewTab ? primaryColor : "rgba(255,255,255,0.12)"}`,
                      borderRadius: 6,
                      color: "#fff",
                      transition: "all 0.12s",
                    }}
                  >
                    <svg
                      width="10"
                      height="8"
                      viewBox="0 0 10 8"
                      style={{
                        opacity: newOpenInNewTab ? 1 : 0,
                        transition: "opacity 0.12s",
                      }}
                    >
                      <path
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M1 4l2.5 2.5L9 1"
                      />
                    </svg>
                  </span>
                </span>
                Open in new tab
              </label>
              <button
                type="button"
                onClick={() => void handleAddLink()}
                disabled={mutLoading || !newLabel.trim() || !newHref.trim()}
                style={{
                  padding: "6px 12px",
                  background: primaryColor,
                  border: "none",
                  borderRadius: 6,
                  color: "#fff",
                  fontSize: 11,
                  cursor: mutLoading ? "default" : "pointer",
                  opacity: mutLoading || !newLabel.trim() || !newHref.trim() ? 0.6 : 1,
                }}
              >
                Add
              </button>
            </div>
          )}

          {items.length === 0 && !addingLink && (
            <p
              style={{
                marginTop: 8,
                fontSize: 11,
                color: "rgba(255,255,255,0.35)",
              }}
            >
              Pages are auto-generated from your site pages. To rename a page link, edit the page title in page settings. Add custom links above to override.
            </p>
          )}
        </div>

        {/* CTA Button Text - Spec Section 13: margin-top 14px, FieldLabel + TinyInput */}
        <div style={{ marginTop: 14 }}>
          <label style={LABEL_STYLE}>Donate button</label>
          <TinyInput
            value={navCtaText}
            onChange={(e) => onUpdateNavCtaText?.(e.target.value)}
            placeholder="Donate"
            style={{ marginTop: 6 }}
          />
          <p style={{ marginTop: 6, fontSize: 10, color: "rgba(255,255,255,0.3)" }}>
            Opens the donation form. Clear the text to hide it.
          </p>
        </div>

        {/* NOS-233: secondary CTA (NOS-215 outline button, e.g. Subscribe) */}
        <div>
          <label style={LABEL_STYLE}>Secondary button</label>
          {secondaryCta || addingSecondaryCta ? (
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              <TinyInput
                value={ctaLabelDraft}
                onChange={(e) => setCtaLabelDraft(e.target.value)}
                onBlur={() => secondaryCta && void handleSaveSecondaryCta()}
                placeholder="Label (e.g. Subscribe)"
              />
              <TinyInput
                value={ctaHrefDraft}
                onChange={(e) => setCtaHrefDraft(e.target.value)}
                onBlur={() => secondaryCta && void handleSaveSecondaryCta()}
                placeholder="/subscribe, https://…, or #anchor"
                style={{ fontSize: 11 }}
              />
              <div style={{ display: "flex", gap: 8 }}>
                {!secondaryCta && (
                  <button
                    type="button"
                    onClick={() => void handleSaveSecondaryCta()}
                    disabled={mutLoading || !ctaLabelDraft.trim() || !ctaHrefDraft.trim()}
                    style={{
                      padding: "6px 12px",
                      background: primaryColor,
                      border: "none",
                      borderRadius: 6,
                      color: "#fff",
                      fontSize: 11,
                      cursor: mutLoading ? "default" : "pointer",
                      opacity: mutLoading || !ctaLabelDraft.trim() || !ctaHrefDraft.trim() ? 0.6 : 1,
                    }}
                  >
                    Add button
                  </button>
                )}
                <button
                  type="button"
                  onClick={() =>
                    secondaryCta ? void handleRemoveSecondaryCta() : setAddingSecondaryCta(false)
                  }
                  style={{
                    padding: "6px 12px",
                    background: "rgba(255,255,255,0.06)",
                    border: "1px solid rgba(255,255,255,0.1)",
                    borderRadius: 6,
                    color: "rgba(255,255,255,0.7)",
                    fontSize: 11,
                    cursor: "pointer",
                  }}
                >
                  {secondaryCta ? "Remove" : "Cancel"}
                </button>
              </div>
            </div>
          ) : (
            <button
              type="button"
              onClick={() => setAddingSecondaryCta(true)}
              style={{
                width: "100%",
                padding: 7,
                background: "rgba(255,255,255,0.02)",
                border: "1px dashed rgba(255,255,255,0.08)",
                borderRadius: 7,
                color: "rgba(255,255,255,0.3)",
                fontSize: 10,
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 6,
              }}
            >
              <span>＋</span>
              <span>Add secondary button (e.g. Subscribe)</span>
            </button>
          )}
        </div>

        {/* NOS-239: announcement bar entry point (bar itself is only clickable
            on the canvas once enabled) */}
        {onOpenAnnouncement && (
          <div>
            <label style={LABEL_STYLE}>Announcement bar</label>
            <button
              type="button"
              onClick={onOpenAnnouncement}
              style={{
                width: "100%",
                padding: 7,
                background: "rgba(255,255,255,0.02)",
                border: "1px dashed rgba(255,255,255,0.08)",
                borderRadius: 7,
                color: "rgba(255,255,255,0.3)",
                fontSize: 10,
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 6,
              }}
            >
              <span>Edit announcement bar (above the menu)</span>
            </button>
          </div>
        )}
      </div>
    </FloatingPanel>
  )
}
